import { useAuth, useClerk, useOrganization, useOrganizationList, useUser } from '@clerk/clerk-react';
import { AppLoader } from '@flobi/asset-library/app-shell';
import { useEffect, type ReactNode } from 'react';
import { setTokenGetter } from '../services/apiClient';
import { redirectToSignIn } from './redirect';
import { touchSession } from './session';

/**
 * Sign-in and workspace, the way every Flobi app does it: Clerk holds the session, the
 * active organisation IS the workspace (there is no personal space), and the gateway
 * reads both from the token. Switching workspace is Clerk's `setActive`; no backend call
 * exists for it. The choice is drawn by Artwork, never by Clerk's own component.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded: authLoaded, getToken } = useAuth();
  const { isLoaded: userLoaded } = useUser();
  const { organization, isLoaded: orgLoaded } = useOrganization();

  // During render, not in an effect: the first calls a child makes must carry a token.
  setTokenGetter(() => getToken());

  useEffect(() => {
    if (authLoaded && !isSignedIn) redirectToSignIn();
  }, [authLoaded, isSignedIn]);

  // The shared curtain while the session resolves, as the other Flobi apps open. After the
  // first load it has retired, so the words underneath are what shows (review H4).
  if (!authLoaded || !userLoaded || !orgLoaded) {
    return (
      <>
        <AppLoader />
        <Centered>Loading…</Centered>
      </>
    );
  }
  if (!isSignedIn) {
    return (
      <>
        <AppLoader caption="Taking you to sign in…" />
        <Centered>Taking you to sign in…</Centered>
      </>
    );
  }
  if (!organization) {
    return (
      <Centered>
        <p className="mb-3">Artwork files belong to a workspace. Choose one to continue.</p>
        <ChooseWorkspace />
      </Centered>
    );
  }
  return <>{children}</>;
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-page p-6 text-center text-sm text-ink-muted">{children}</div>
  );
}

/**
 * The workspaces this person is in, as a list to pick from — Artwork's own, never the
 * sign-in provider's component. Clerk only answers who and which (`useOrganizationList`)
 * and switches (`setActive`).
 */
function ChooseWorkspace() {
  const { userMemberships, setActive, isLoaded } = useOrganizationList({ userMemberships: true });
  const clerk = useClerk();
  const orgs = (userMemberships?.data ?? []).map((membership) => membership.organization);
  if (!isLoaded) return null;
  if (orgs.length === 0) {
    return <p className="max-w-[320px]">You aren’t in a workspace yet. Ask someone to invite you, or make one in Flobi Home.</p>;
  }
  return (
    <ul className="flex w-[min(320px,100%)] flex-col gap-[4px]" aria-label="Workspaces">
      {orgs.map((org) => (
        <li key={org.id}>
          <button
            type="button"
            onClick={() => void setActive?.({ organization: org.id }).then(() => touchSession(clerk))}
            className="flex w-full items-center gap-[10px] rounded-[10px] px-[10px] py-[8px] text-left text-[13px] font-medium text-ink hover:bg-fill-2"
          >
            {org.hasImage ? (
              <img src={org.imageUrl} alt="" className="size-[24px] shrink-0 rounded-[7px] object-cover" />
            ) : (
              <span className="grid size-[24px] shrink-0 place-items-center rounded-[7px] bg-[#8b5cf6] text-[11px] font-semibold text-white">
                {org.name.trim().charAt(0).toUpperCase() || '?'}
              </span>
            )}
            <span className="min-w-0 flex-1 truncate">{org.name}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
